import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import {
  addToAgenda,
  cabMeetingQuery,
  decideOnAgenda,
  removeCabMeeting,
  removeFromAgenda,
  saveCabMeeting,
  type AgendaItem,
  type CabMeetingDetail,
  type MeetingDecision,
  type MeetingStatus,
} from '../api/cabMeetings'
import { ApiError } from '../api/client'
import { LoadFailed } from '../ui/LoadFailed'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import { failedToast, when } from './CabMeetingsPage'
import './CabMeetings.css'

/**
 * O sedinta CAB: datele ei, participantii si prezenta, agenda de change-uri cu
 * hotararea pe fiecare, si minuta.
 *
 * Hotararea si aprobarea: "Approve" / "Reject" se scriu in minuta; cine e
 * membru al CAB-ului change-ului le poate da si ca VOTUL LUI (casuta "Also cast
 * as my vote"), pe drumul obisnuit al aprobarii. Un vot refuzat nu scrie nici
 * minuta. Starea aprobarii change-ului se vede langa fiecare rand.
 */

const APPROVAL_LABEL: Record<string, string> = {
  draft: 'Draft',
  pending: 'Waiting for the CAB',
  approved: 'Approved',
  rejected: 'Rejected',
}

export function CabMeetingPage({ id }: { id: number }) {
  const page = useQuery(cabMeetingQuery(id))
  const queryClient = useQueryClient()
  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['easeedesk', 'cab_meeting', id] })
    void queryClient.invalidateQueries({ queryKey: ['easeedesk', 'cab_meetings'] })
  }

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error instanceof ApiError && page.error.status === 404) {
    return (
      <div className="alert alert-warning od-cab-meeting-missing">
        No such CAB meeting. <a href="/app/easeedesk/cab_meetings">All CAB meetings</a>
      </div>
    )
  }
  if (page.error) return <LoadFailed what="CAB meeting" error={page.error} onRetry={() => void page.refetch()} />
  if (!page.data) return <Spinner />

  return (
    <div className="od-cab-meeting">
      <p>
        <a href="/app/easeedesk/cab_meetings">
          <i className="fas fa-arrow-left" aria-hidden="true"></i> All CAB meetings
        </a>
      </p>
      <Header page={page.data} onSaved={reload} />
      <Participants page={page.data} onSaved={reload} />
      <Agenda page={page.data} onSaved={reload} />
      <Minutes page={page.data} onSaved={reload} />
    </div>
  )
}

/* ------------------------------------------------------------------ */

function Header({ page, onSaved }: { page: CabMeetingDetail; onSaved(): void }) {
  const m = page.meeting
  const navigate = useNavigate()
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({ title: m.title, meeting_at: m.meeting_at, location: m.location, cab_group_id: m.cab_group_id })

  const save = useMutation({
    mutationFn: (fields: Parameters<typeof saveCabMeeting>[1]) => saveCabMeeting(m.ID, fields),
    onSuccess: (answer) => {
      toast(answer.message, 'CAB meeting')
      setEditing(false)
      onSaved()
    },
    onError: failedToast,
  })

  const remove = useMutation({
    mutationFn: () => removeCabMeeting(m.ID),
    onSuccess: (answer) => {
      toast(answer.message, 'CAB meeting')
      void navigate({ to: '/easeedesk/cab_meetings' })
    },
    onError: failedToast,
  })

  return (
    <section className="od-cab-meeting-head">
      {page.canEdit && !editing && (
        <div className="pull-right">
          <select
            className="form-control input-sm od-cab-status"
            aria-label="State"
            value={m.status}
            disabled={save.isPending}
            onChange={(e) => save.mutate({ status: e.target.value as MeetingStatus })}
            style={{ display: 'inline-block', width: 'auto' }}
          >
            {page.statuses.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>{' '}
          <button type="button" className="btn btn-default btn-sm od-cab-edit" onClick={() => setEditing(true)}>
            <i className="fas fa-pencil-alt" aria-hidden="true"></i> Edit
          </button>{' '}
          <button
            type="button"
            className="btn btn-default btn-sm od-cab-remove"
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm('Remove this CAB meeting?')) remove.mutate()
            }}
          >
            <i className="fas fa-times" aria-hidden="true"></i> Remove
          </button>
        </div>
      )}
      <h4>
        <strong>{m.title}</strong> <span className={'label od-cab-status-' + m.status}>{m.status_label}</span>
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        {when(m.meeting_at)}
        {m.location !== '' && <> &middot; {m.location}</>}
        {m.cab_group_name !== '' && <> &middot; {m.cab_group_name}</>}
      </label>
      <hr />

      {editing && (
        <form
          className="od-cab-meeting-form well well-sm"
          onSubmit={(e) => {
            e.preventDefault()
            save.mutate(form)
          }}
        >
          <div className="row">
            <div className="col-md-4 form-group">
              <label htmlFor="od-cab-edit-title">Title</label>
              <input
                id="od-cab-edit-title"
                className="form-control"
                maxLength={150}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>
            <div className="col-md-3 form-group">
              <label htmlFor="od-cab-edit-when">Date and time</label>
              <input
                id="od-cab-edit-when"
                type="datetime-local"
                className="form-control"
                value={form.meeting_at}
                onChange={(e) => setForm({ ...form, meeting_at: e.target.value })}
              />
            </div>
            <div className="col-md-3 form-group">
              <label htmlFor="od-cab-edit-where">Location</label>
              <input
                id="od-cab-edit-where"
                className="form-control"
                maxLength={255}
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
              />
            </div>
            <div className="col-md-2 form-group">
              <label htmlFor="od-cab-edit-group">CAB group</label>
              <select
                id="od-cab-edit-group"
                className="form-control"
                value={form.cab_group_id === 0 ? '' : String(form.cab_group_id)}
                onChange={(e) => setForm({ ...form, cab_group_id: Number(e.target.value) || 0 })}
              >
                <option value="">None</option>
                {page.groups.map((g) => (
                  <option key={g.ID} value={g.ID}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button type="submit" className="btn btn-primary btn-sm" disabled={save.isPending}>
            Save
          </button>{' '}
          <button type="button" className="btn btn-default btn-sm" onClick={() => setEditing(false)}>
            Cancel
          </button>
        </form>
      )}
    </section>
  )
}

/* ------------------------------------------------------------------ */

function Participants({ page, onSaved }: { page: CabMeetingDetail; onSaved(): void }) {
  const [editing, setEditing] = useState(false)
  const [chosen, setChosen] = useState<number[]>(page.participants.map((p) => p.user_id))

  const save = useMutation({
    mutationFn: (fields: { participants?: number[]; attended?: number[] }) => saveCabMeeting(page.meeting.ID, fields),
    onSuccess: (answer) => {
      toast(answer.message, 'CAB meeting')
      setEditing(false)
      onSaved()
    },
    onError: failedToast,
  })

  const came = page.participants.filter((p) => p.attended).map((p) => p.user_id)
  const toggleAttended = (userId: number) =>
    save.mutate({ attended: came.includes(userId) ? came.filter((u) => u !== userId) : [...came, userId] })

  return (
    <section className="od-cab-section od-cab-participants">
      <h5>
        <strong>Participants</strong>{' '}
        {page.canEdit && !editing && (
          <button type="button" className="btn btn-default btn-xs od-cab-participants-edit" onClick={() => setEditing(true)}>
            <i className="fas fa-pencil-alt" aria-hidden="true"></i> Change
          </button>
        )}
      </h5>

      {editing ? (
        <div className="well well-sm">
          {page.staff.map((p) => (
            <label key={p.ID} className="checkbox-inline">
              <input
                type="checkbox"
                checked={chosen.includes(p.ID)}
                onChange={() => setChosen(chosen.includes(p.ID) ? chosen.filter((u) => u !== p.ID) : [...chosen, p.ID])}
              />{' '}
              {p.name}
            </label>
          ))}
          <div style={{ marginTop: 8 }}>
            <button type="button" className="btn btn-primary btn-sm" disabled={save.isPending} onClick={() => save.mutate({ participants: chosen })}>
              Save
            </button>{' '}
            <button type="button" className="btn btn-default btn-sm" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : page.participants.length === 0 ? (
        <p className="color-light-grey">Nobody is invited yet.</p>
      ) : (
        <ul className="list-unstyled od-cab-people-list">
          {page.participants.map((p) => (
            <li key={p.user_id}>
              <label className="od-cab-attended">
                <input
                  type="checkbox"
                  checked={p.attended}
                  disabled={!page.canEdit || save.isPending}
                  onChange={() => toggleAttended(p.user_id)}
                />{' '}
                {p.name || 'user #' + p.user_id}
              </label>{' '}
              <span className="color-light-grey">{p.attended ? 'attended' : 'invited'}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/* ------------------------------------------------------------------ */

function Agenda({ page, onSaved }: { page: CabMeetingDetail; onSaved(): void }) {
  const [adding, setAdding] = useState('')
  const meetingId = page.meeting.ID

  const add = useMutation({
    mutationFn: (ticket: number) => addToAgenda(meetingId, ticket),
    onSuccess: (answer) => {
      toast(answer.message, 'CAB meeting')
      setAdding('')
      onSaved()
    },
    onError: failedToast,
  })

  const remove = useMutation({
    mutationFn: (ticket: number) => removeFromAgenda(meetingId, ticket),
    onSuccess: (answer) => {
      toast(answer.message, 'CAB meeting')
      onSaved()
    },
    onError: failedToast,
  })

  return (
    <section className="od-cab-section od-cab-agenda">
      <h5>
        <strong>Agenda</strong>
      </h5>

      {page.agenda.length === 0 ? (
        <p className="color-light-grey od-cab-agenda-empty">No change on the agenda yet.</p>
      ) : (
        <div className="od-cab-table-wrap">
          <table className="table od-cab-agenda-table">
            <thead>
              <tr>
                <th>Change</th>
                <th>Approval</th>
                <th>Decision</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {page.agenda.map((item) => (
                <AgendaRow
                  key={item.ticket_id}
                  meetingId={meetingId}
                  item={item}
                  canEdit={page.canEdit}
                  decisions={page.decisions}
                  onSaved={onSaved}
                  onRemove={() => remove.mutate(item.ticket_id)}
                  removing={remove.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {page.canEdit && (
        <form
          className="form-inline od-cab-agenda-add"
          onSubmit={(e) => {
            e.preventDefault()
            const ticket = Number(adding)
            if (ticket > 0) add.mutate(ticket)
          }}
        >
          <label htmlFor="od-cab-agenda-pick">Add a change</label>{' '}
          <select
            id="od-cab-agenda-pick"
            className="form-control input-sm"
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
          >
            <option value="">
              {page.candidates.length === 0 ? 'No change is waiting for a decision' : 'Choose a change waiting for a decision...'}
            </option>
            {page.candidates.map((c) => (
              <option key={c.ticket_id} value={c.ticket_id}>
                #{c.ticket_id} {c.subject} ({APPROVAL_LABEL[c.approval_state] ?? c.approval_state})
              </option>
            ))}
          </select>{' '}
          <button type="submit" className="btn btn-success btn-sm" disabled={adding === '' || add.isPending}>
            <i className="fas fa-plus" aria-hidden="true"></i> Add
          </button>
        </form>
      )}
    </section>
  )
}

function AgendaRow({
  meetingId,
  item,
  canEdit,
  decisions,
  onSaved,
  onRemove,
  removing,
}: {
  meetingId: number
  item: AgendaItem
  canEdit: boolean
  decisions: { value: string; label: string }[]
  onSaved(): void
  onRemove(): void
  removing: boolean
}) {
  const [decision, setDecision] = useState<MeetingDecision>(item.decision)
  const [notes, setNotes] = useState(item.notes)
  const [castVote, setCastVote] = useState(item.canVote === true)
  const [open, setOpen] = useState(false)

  const save = useMutation({
    mutationFn: () =>
      decideOnAgenda(meetingId, item.ticket_id, decision, notes, castVote && item.canVote === true && (decision === 'approve' || decision === 'reject')),
    onSuccess: (answer) => {
      toast(answer.message, 'CAB meeting')
      setOpen(false)
      onSaved()
    },
    onError: failedToast,
  })

  const votable = decision === 'approve' || decision === 'reject'

  return (
    <tr className="od-cab-agenda-row">
      <td>
        <a href={`/app/easeedesk/ticket/${item.ticket_id}`}>#{item.ticket_id}</a>{' '}
        {item.hidden ? (
          <span className="color-light-grey">not in your companies</span>
        ) : (
          <>
            <strong>{item.subject}</strong>
            <div className="color-light-grey">
              {[item.class_label, item.risk_label !== '' ? item.risk_label + ' risk' : '', item.planned_start ? when(item.planned_start) : '']
                .filter((s) => s !== '' && s !== undefined)
                .join(' · ')}
            </div>
          </>
        )}
      </td>
      <td>
        {!item.hidden && (
          <span className={'od-cab-approval od-cab-approval-' + (item.approval_state ?? 'draft')}>
            {APPROVAL_LABEL[item.approval_state ?? 'draft'] ?? item.approval_state}
            {(item.approval_round ?? 0) > 0 && <span className="color-light-grey"> (round {item.approval_round})</span>}
          </span>
        )}
      </td>
      <td>
        {open ? (
          <form
            className="od-cab-decision-form"
            onSubmit={(e) => {
              e.preventDefault()
              save.mutate()
            }}
          >
            <select
              className="form-control input-sm"
              aria-label="Decision"
              value={decision}
              onChange={(e) => setDecision(e.target.value as MeetingDecision)}
            >
              <option value="">No decision</option>
              {decisions.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
            <textarea
              className="form-control input-sm"
              aria-label="Notes"
              rows={2}
              maxLength={4000}
              placeholder="Notes for the minutes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            {item.canVote === true && votable && (
              <label className="od-cab-cast-vote">
                <input type="checkbox" checked={castVote} onChange={(e) => setCastVote(e.target.checked)} /> Also cast as my vote on
                the change
              </label>
            )}
            <div>
              <button type="submit" className="btn btn-primary btn-xs od-cab-decision-save" disabled={save.isPending}>
                Save
              </button>{' '}
              <button type="button" className="btn btn-default btn-xs" onClick={() => setOpen(false)}>
                Cancel
              </button>
            </div>
          </form>
        ) : item.decision === '' ? (
          <span className="color-light-grey">-</span>
        ) : (
          <>
            <strong className={'od-cab-decision-' + item.decision}>{item.decision_label}</strong>
            {item.decision_round !== null && item.decision_round > 0 && (
              <span className="color-light-grey"> (round {item.decision_round})</span>
            )}
            {item.notes !== '' && <div className="od-cab-notes">{item.notes}</div>}
            <div className="color-light-grey">
              {item.decided_by_name}
              {item.decided_at !== '' && ', ' + when(item.decided_at.slice(0, 16))}
            </div>
          </>
        )}
      </td>
      <td className="aright" style={{ whiteSpace: 'nowrap' }}>
        {canEdit && !open && !item.hidden && (
          <button type="button" className="btn btn-default btn-xs od-cab-decide" onClick={() => setOpen(true)}>
            <i className="fas fa-gavel" aria-hidden="true"></i> Decide
          </button>
        )}{' '}
        {canEdit && !open && (
          <button type="button" className="btn btn-default btn-xs od-cab-agenda-remove" disabled={removing} onClick={onRemove}>
            <i className="fas fa-times" aria-hidden="true"></i>
            <span className="sr-only">Take off the agenda</span>
          </button>
        )}
      </td>
    </tr>
  )
}

/* ------------------------------------------------------------------ */

function Minutes({ page, onSaved }: { page: CabMeetingDetail; onSaved(): void }) {
  const [text, setText] = useState(page.meeting.minutes)

  const save = useMutation({
    mutationFn: () => saveCabMeeting(page.meeting.ID, { minutes: text }),
    onSuccess: (answer) => {
      toast(answer.message, 'CAB meeting')
      onSaved()
    },
    onError: failedToast,
  })

  return (
    <section className="od-cab-section od-cab-minutes">
      <h5>
        <strong>Minutes</strong>
      </h5>
      {page.canEdit ? (
        <>
          <textarea
            className="form-control od-cab-minutes-text"
            aria-label="Minutes"
            rows={6}
            maxLength={20000}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <button
            type="button"
            className="btn btn-primary btn-sm od-cab-minutes-save"
            style={{ marginTop: 6 }}
            disabled={save.isPending || text === page.meeting.minutes}
            onClick={() => save.mutate()}
          >
            Save the minutes
          </button>
        </>
      ) : page.meeting.minutes === '' ? (
        <p className="color-light-grey">No minutes yet.</p>
      ) : (
        <div className="od-cab-minutes-read" style={{ whiteSpace: 'pre-wrap' }}>
          {page.meeting.minutes}
        </div>
      )}
    </section>
  )
}
